import CONFIG from "@/assets/js/config.custom";
import ACCESS_CONTROL from "@/utils/access-control";

const superAdminNo = "0001";

const getUser = (userOverride) => {
    if (userOverride) {
        return userOverride;
    }
    if (typeof window !== 'undefined') {
        if (window.$store && window.$store.state && window.$store.state.user) {
            return window.$store.state.user;
        }
        if (window.vm && window.vm.$store && window.vm.$store.state && window.vm.$store.state.user) {
            return window.vm.$store.state.user;
        }
    }
    return null;
};

const getUserRole = (user) => {
    return user?.role;
};

const getUserAdminNo = (user) => {
    return user?.admin_no;
};

const isSuperAdmin = (user) => {
    return getUserAdminNo(user) === superAdminNo || getUserRole(user) === CONFIG.ROLE.ID.SUPER_ADMIN;
};

const hasAccess = (user, module, action) => {
    const userRole = getUserRole(user);
    if (isSuperAdmin(user) || userRole === CONFIG.ROLE.ID.ADMIN) {
        return true;
    }
    const permissions = ACCESS_CONTROL[module]?.[action];
    return permissions ? permissions.includes(userRole) : false;
};

/**
 * Common payment action permission checker (same logic as payment-list.js)
 * @param {string} action - edit/delete
 * @param {number} paymentStatus
 * @param {number} historyStatus
 * @param {object} user - optional user object (for use in Vue component, pass this.$store.state.user)
 * @returns {boolean}
 */
export const canManagePayment = (action, historyStatus, user) => {
    historyStatus = Number(historyStatus);

    const currentUser = user || getUser();
    if (!currentUser) return false;
    const userRole = currentUser?.role;
    const isSuperAdminNo = isSuperAdmin(currentUser);
    if (!hasAccess(currentUser, 'PAYMENT', action)) {
        return false;
    }

    const isFullyPaid = historyStatus === CONFIG.PARENT_PAYMENT_STATUS.FULLY_PAID;

    if (isFullyPaid) {
        const isRestrictedRole = userRole === CONFIG.ROLE.ID.ACCOUNTANT || userRole === CONFIG.ROLE.ID.ADMIN;
        if (!isSuperAdminNo && isRestrictedRole) {
            return false;
        }
    }
    return true;
};

/**
 * Check if user can manage payment history (same logic as payment-detail.js & tuition-payment-detail.js)
 * @param {string} action - create/edit/delete/approve/reject
 * @param {object} history - payment history object
 * @param {object} user - optional user object (for use in Vue component, pass this.$store.state.user)
 * @returns {boolean}
 */
export const canManageHistory = (action, history, user) => {
    const currentUser = user || getUser();
    if (!currentUser) return false;
    const userRole = Number(currentUser?.role);
    const isSuperAdminNo = isSuperAdmin(currentUser);

    if (isSuperAdminNo) {
        return true;
    }
    if (!hasAccess(currentUser, 'PAYMENT_HISTORY', action)) {
        return false;
    }
    const historyStatus = Number(history?.status);
    if (historyStatus === CONFIG.PAYMENT_STATUS.STATUS_APPROVED || historyStatus === CONFIG.PAYMENT_STATUS.STATUS_REJECTED) {
        return userRole === CONFIG.ROLE.ID.PRINCIPAL;
    }
    return true;
};

/**
 * Wrapper function for use in Vue template
 * Use this in template: v-if="canManagePayment('delete', payment_status, history_status)"
 */
export const canManagePaymentFn = function(action, paymentStatus, historyStatus) {
    return canManagePayment(action, paymentStatus, historyStatus);
};

/**
 * Wrapper function for use in Vue template
 * Use this in template: v-if="canManageHistoryFn('edit', history)"
 */
export const canManageHistoryFn = function(action, history) {
    return canManageHistory(action, history);
};

export default {
    canManagePayment,
    canManageHistory,
    canManagePaymentFn,
    canManageHistoryFn
};
